import React, { useEffect } from 'react';
import { VideoTrack } from '@livekit/components-react';
import { ParticipantVideoProps, PanelPosition } from '../types/LiveKitVideoDisplay.types';
import {
  DynamicVideoWrapper,
  ScreenShareOverlay,
  ScreenShareLabel,
  SidebarVideoWrapper,
  SidebarParticipantName,
  LocalSidebarVideoWrapper
} from '../styles/LiveKitVideoDisplay.styles';
import { VideoControlProps } from '../types/LiveKitVideoDisplay.types';
import { MicOffIcon, CameraOffIcon, CameraOnIcon } from '../../ScreenShare/VideoControlButton';
import { HandIcon, HandRaisedIndicator, handBadgeProps } from '../../ScreenShare/HandRaisedIndicator';
import {
  CAMERA_WAITING_COLOR,
  CameraVideoFrame,
  CameraWaitingPlaceholder,
  cameraWaitingLabel,
  VIDEO_TILE_COVER_BACKGROUND,
} from './CameraWaitingPlaceholder';
import { useCameraTilePicture } from './useCameraTilePicture';
import { CameraOffPicture, useCameraOffPicture } from '../cameraOffPicture';
import { GalleryVideoWrapper } from '../styles/GalleryVideoDisplay.styles';
import { selfViewVideoStyle } from '../../../utils/selfViewMirror';

/** The waiting indicator: about a third of the tile's height (60px collapsed, 120px otherwise). */
const WAITING_ICON_PX_COLLAPSED = 20;
const WAITING_ICON_PX = 28;

/**
 * A `<video>` under a waiting cover, before its first frame: see-through
 * rather than LiveKit's opaque black, so mounting it does not darken the cover
 * over it. Once it has painted, its frames are opaque anyway.
 */
const UNPAINTED_VIDEO_STYLE: React.CSSProperties = { backgroundColor: 'transparent' };

export interface ParticipantVideoOverlayProps extends ParticipantVideoProps {
  isVisible?: boolean;
  videoControls: VideoControlProps;
  participants: any[];
  screenShareTracks: any[];
  /** Pre-filtered camera track for this participant (from useVirtualRoomFilteredParticipants). Pass undefined if not in same room. */
  cameraTrack?: any;
  /** Session ID of the virtual room owner; when participantId matches, show owner icon */
  ownerSessionId?: string;
  /** Panel position for sidebar variant (right/top/left) */
  panelPosition?: PanelPosition;
  /** Whether this participant is using a whiteboard */
  isUsingWhiteboard?: boolean;
  /** Callback when user clicks the whiteboard indicator on this tile */
  onWhiteboardClick?: (participantId: string) => void;
  /** Whether this participant has a raised hand */
  isHandRaised?: boolean;
  /** Set only when the local user may lower this participant's hand (owner / presenter) */
  onLowerHand?: (participantId: string) => void;
  /** SC-375: `'gallery'` fills its grid cell instead of the fixed sidebar size. */
  variant?: 'sidebar' | 'gallery';
}

const TileHandBadge: React.FC<{
  participantId: string;
  onLowerHand?: (participantId: string) => void;
  style: React.CSSProperties;
}> = ({ participantId, onLowerHand, style }) => {
  const badge = handBadgeProps(onLowerHand ? () => onLowerHand(participantId) : undefined);
  return (
    <HandRaisedIndicator
      data-testid={`tile-hand-raised-${participantId}`}
      {...badge}
      style={{ ...badge.style, ...style }}
    >
      <HandIcon />
    </HandRaisedIndicator>
  );
};

export const ParticipantVideoOverlay = React.forwardRef<HTMLDivElement, ParticipantVideoOverlayProps>(({
  participantId,
  isLocal,
  isSpeaking,
  isScreenSharing,
  position,
  isVisible = true,
  isExpanded,
  onHover,
  onScreenShareClick,
  videoControls,
  participants,
  screenShareTracks,
  cameraTrack,
  ownerSessionId,
  isUsingWhiteboard,
  onWhiteboardClick,
  isHandRaised,
  onLowerHand,
}, ref) => {
  // Use the pre-filtered camera track from parent (already room-isolated).
  // Never call useTracks() here — it returns ALL subscribed tracks from the LiveKit room.
  const participant = participants.find((p) => p.identity === participantId);
  const {
    videoTrack,
    waiting,
    problem: localProblem,
    showsPicture,
    covered,
    coverPhase,
    videoRef,
  } = useCameraTilePicture({
    participantId,
    isLocal,
    videoEnabled: videoControls.videoEnabled,
    cameraTrack,
    participant,
  });
  const cameraOffPicture = useCameraOffPicture(participant, isLocal, participantId.substring(0, 8));

  // Retry: if a remote participant is in proximity but their video track is missing
  // (subscription race), trigger a recalculation after a short delay so the
  // subscription manager resubscribes their video track.
  // Must be before early return (Rules of Hooks).
  const trackMissing = !cameraTrack;
  useEffect(() => {
    if (!isLocal && trackMissing) {
      const timer = setTimeout(() => {
        window.dispatchEvent(new CustomEvent('force-proximity-recalculation'));
      }, 400);
      return () => clearTimeout(timer);
    }
  }, [isLocal, trackMissing]);

  if (!position) return null;

  const borderColor = isLocal
    ? '2px solid rgba(0, 200, 0, 0.8)' // More visible border for local video
    : '2px solid rgba(255, 255, 255, 0.3)';

  // Create a more stable key that includes participant ID
  const stableKey = `overlay-${isLocal ? 'local' : 'remote'}-${participantId}`;

  return (
    <DynamicVideoWrapper
      ref={ref}
      key={stableKey}
      isExpanded={isExpanded}
      isLocal={isLocal}
      isScreenSharing={isScreenSharing}
      style={{
        border: isSpeaking ? undefined : borderColor, // Don't override speaking border
        display: isVisible ? 'flex' : 'none',
      }}
      className={`${isSpeaking ? 'speaking' : ''} ${isScreenSharing ? 'screen-sharing' : ''}`}
      onMouseEnter={() => onHover(participantId, true)}
      onMouseLeave={() => onHover(participantId, false)}
    >
      {/* Render video track if available, otherwise show placeholder */}
      {showsPicture && (
        <CameraVideoFrame>
          {/* @ts-ignore - VideoTrack returns ReactNode but we need JSX.Element */}
          <VideoTrack
            trackRef={videoTrack!}
            ref={videoRef}
            style={selfViewVideoStyle(
              isLocal,
              coverPhase === 'covering' ? UNPAINTED_VIDEO_STYLE : undefined
            )}
          />
        </CameraVideoFrame>
      )}
      {/* After the video, so it lies on top until the video has painted. */}
      {covered || coverPhase ? (
        <CameraWaitingPlaceholder
          participantId={participantId}
          problem={localProblem}
          settles={!isLocal}
          phase={coverPhase ?? 'covering'}
          iconSize={isExpanded ? WAITING_ICON_PX : WAITING_ICON_PX_COLLAPSED}
        />
      ) : showsPicture ? null : (
        <div
          data-testid={`camera-off-${participantId}`}
          style={{
            width: '100%',
            height: '100%',
            backgroundColor: VIDEO_TILE_COVER_BACKGROUND,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <CameraOffPicture picture={cameraOffPicture} />
        </div>
      )}

      {/* Add "YOU" label for local video */}
      {isLocal && (
        <div
          style={{
            position: 'absolute',
            top: '4px',
            left: '4px',
            background: 'rgba(0, 200, 0, 0.9)',
            color: 'white',
            padding: '2px 6px',
            borderRadius: '4px',
            fontSize: '10px',
            fontWeight: 'bold',
            zIndex: 5,
            textShadow: '0 1px 1px rgba(0, 0, 0, 0.5)',
          }}
        >
          YOU
        </div>
      )}

      {/* Add "Owner" label for room owner (overlay mode) */}
      {!isLocal && ownerSessionId && participantId === ownerSessionId && (
        <div
          style={{
            position: 'absolute',
            top: '4px',
            left: '4px',
            background: 'rgba(249, 116, 21, 0.9)',
            color: 'white',
            padding: '2px 6px',
            borderRadius: '4px',
            fontSize: '10px',
            fontWeight: 'bold',
            zIndex: 5,
            textShadow: '0 1px 1px rgba(0, 0, 0, 0.5)',
          }}
        >
          Owner
        </div>
      )}

      {/* Add "SHARING" label for screen sharing video */}
      {isScreenSharing && (
        <div
          style={{
            position: 'absolute',
            top: '4px',
            right: '4px',
            background: 'rgba(255, 165, 0, 0.9)',
            color: 'white',
            padding: '2px 6px',
            borderRadius: '4px',
            fontSize: '10px',
            fontWeight: 'bold',
            zIndex: 5,
            textShadow: '0 1px 1px rgba(0, 0, 0, 0.5)',
          }}
        >
          SHARING
        </div>
      )}

      {/* Show screen share overlay for any participant who is sharing */}
      {isScreenSharing && (
        <ScreenShareOverlay
          className="screen-share-overlay"
          onClick={() => {
            // Find the screen share track for this participant
            const screenTrack = screenShareTracks.find(
              (t) => t.participant.identity === participantId
            );
            if (screenTrack) {
              onScreenShareClick(participantId, screenTrack.trackSid);
            }
          }}
        >
          {(() => {
            const track = screenShareTracks.find(
              (t) => t.participant.identity === participantId
            );
            return track ? (
              <div style={{ width: '100%', height: '100%' }}>
                {/* @ts-ignore - VideoTrack returns ReactNode but we need JSX.Element */}
                <VideoTrack trackRef={track} />
              </div>
            ) : (
              <div
                style={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: isExpanded ? '12px' : '8px',
                }}
              >
                📺
              </div>
            );
          })()}
          <ScreenShareLabel className="screen-share-label">
            {isLocal ? 'YOUR' : 'SHARED'}
          </ScreenShareLabel>
        </ScreenShareOverlay>
      )}

      {/* Show whiteboard overlay for any participant using a whiteboard (same pattern as screenshare) */}
      {isUsingWhiteboard && !isScreenSharing && (
        <ScreenShareOverlay
          className="screen-share-overlay"
          onClick={() => onWhiteboardClick?.(participantId)}
          style={{
            border: '2px solid rgba(99, 102, 241, 0.8)',
          }}
        >
          <div
            style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontSize: isExpanded ? '16px' : '12px',
            }}
          >
            📝
          </div>
          <ScreenShareLabel className="screen-share-label" style={{ background: 'rgba(99, 102, 241, 0.9)', fontSize: '6px' }}>
            WHITEBOARD
          </ScreenShareLabel>
        </ScreenShareOverlay>
      )}

      {/* Bottom-left and above the share/whiteboard thumbnail, which covers most of a small tile */}
      {isHandRaised && (
        <TileHandBadge
          participantId={participantId}
          onLowerHand={onLowerHand}
          style={{ top: 'auto', bottom: '4px', left: '4px', width: '20px', height: '20px', zIndex: 16 }}
        />
      )}
    </DynamicVideoWrapper>
  );
});

// Sidebar variant of the participant video component
export const ParticipantVideoSidebar = React.forwardRef<HTMLDivElement, ParticipantVideoOverlayProps>(({
  participantId,
  isLocal,
  isSpeaking,
  isScreenSharing,
  onScreenShareClick,
  videoControls,
  participants,
  screenShareTracks,
  cameraTrack,
  ownerSessionId,
  panelPosition,
  isUsingWhiteboard,
  onWhiteboardClick,
  isHandRaised,
  onLowerHand,
  variant = 'sidebar',
}, ref) => {
  // Use the pre-filtered camera track from parent (already room-isolated).
  // Never call useTracks() here — it returns ALL subscribed tracks from the LiveKit room.
  const participant = participants.find(p => p.identity === participantId);
  const {
    videoTrack,
    waiting,
    problem: localProblem,
    showsPicture,
    covered,
    coverPhase,
    videoRef,
  } = useCameraTilePicture({
    participantId,
    isLocal,
    videoEnabled: videoControls.videoEnabled,
    cameraTrack,
    participant,
  });

  const participantName = participant?.name || participantId.substring(0, 8);
  const cameraOffPicture = useCameraOffPicture(participant, isLocal, participantName);
  const isMuted = isLocal ? !videoControls.audioEnabled : (participant ? !participant.isMicrophoneEnabled : false);
  const isCameraDisabled = isLocal ? !videoControls.videoEnabled : (participant ? !participant.isCameraEnabled : false);

  // Retry: if a remote participant is in proximity but their video track is missing
  // (subscription race), trigger a recalculation after a short delay.
  const trackMissing = !cameraTrack;
  useEffect(() => {
    if (!isLocal && trackMissing) {
      const timer = setTimeout(() => {
        window.dispatchEvent(new CustomEvent('force-proximity-recalculation'));
      }, 400);
      return () => clearTimeout(timer);
    }
  }, [isLocal, trackMissing]);


  // Use the appropriate wrapper based on whether it's local or not
  const VideoWrapper = variant === 'gallery'
    ? GalleryVideoWrapper
    : isLocal ? LocalSidebarVideoWrapper : SidebarVideoWrapper;

  return (
    <VideoWrapper
      ref={ref}
      panelPosition={panelPosition}
      className={`${isSpeaking ? 'speaking' : ''} ${isScreenSharing ? 'screen-sharing' : ''}`}
    >
      {/* Render video track if available, otherwise show placeholder */}
      {showsPicture && (
        <CameraVideoFrame>
          {/* @ts-ignore - VideoTrack returns ReactNode but we need JSX.Element */}
          <VideoTrack
            trackRef={videoTrack!}
            ref={videoRef}
            style={selfViewVideoStyle(
              isLocal,
              coverPhase === 'covering' ? UNPAINTED_VIDEO_STYLE : undefined
            )}
          />
        </CameraVideoFrame>
      )}
      {/* After the video, so it lies on top until the video has painted. */}
      {covered || coverPhase ? (
        <CameraWaitingPlaceholder
          participantId={participantId}
          problem={localProblem}
          settles={!isLocal}
          phase={coverPhase ?? 'covering'}
          iconSize={WAITING_ICON_PX}
        />
      ) : showsPicture ? null : (
        <div
          data-testid={`camera-off-${participantId}`}
          style={{
            width: '100%',
            height: '100%',
            backgroundColor: VIDEO_TILE_COVER_BACKGROUND,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <CameraOffPicture picture={cameraOffPicture} />
        </div>
      )}

      {/* Add "YOU" label for local video */}
      {isLocal && (
        <div
          style={{
            position: 'absolute',
            top: '4px',
            left: '4px',
            background: 'rgba(0, 200, 0, 0.9)',
            color: 'white',
            padding: '2px 6px',
            borderRadius: '4px',
            fontSize: '10px',
            fontWeight: 'bold',
            zIndex: 5,
            textShadow: '0 1px 1px rgba(0, 0, 0, 0.5)',
          }}
        >
          YOU
        </div>
      )}

      {/* Media indicators in top right */}
      <div
        style={{
          position: 'absolute',
          top: '4px',
          right: '4px',
          display: 'flex',
          gap: '4px',
          zIndex: 5,
        }}
      >
        {isMuted && (
          <div
            data-testid={`sidebar-muted-${participantId}`}
            style={{
              background: 'rgba(244, 67, 54, 0.9)',
              color: 'white',
              padding: '2px 4px',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '11px',
            }}
            title="Muted"
          >
            <MicOffIcon />
          </div>
        )}
        {waiting && (
          <div
            data-testid={`sidebar-camera-waiting-${participantId}`}
            style={{
              background: CAMERA_WAITING_COLOR,
              color: 'var(--sevv-on-warning, #ffffff)',
              padding: '2px 4px',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '11px',
            }}
            title={cameraWaitingLabel(localProblem)}
          >
            <CameraOnIcon />
          </div>
        )}
        {isCameraDisabled && !waiting && (
          <div
            data-testid={`sidebar-camera-disabled-${participantId}`}
            style={{
              background: 'rgba(244, 67, 54, 0.9)',
              color: 'white',
              padding: '2px 4px',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '11px',
            }}
            title="Camera disabled"
          >
            <CameraOffIcon />
          </div>
        )}
        {isScreenSharing && (
          <div
            style={{
              background: 'rgba(255, 165, 0, 0.9)',
              color: 'white',
              padding: '2px 6px',
              borderRadius: '4px',
              fontSize: '10px',
              fontWeight: 'bold',
              textShadow: '0 1px 1px rgba(0, 0, 0, 0.5)',
            }}
          >
            SHARING
          </div>
        )}
        {isHandRaised && (
          <TileHandBadge
            participantId={participantId}
            onLowerHand={onLowerHand}
            style={{ position: 'static', width: '20px', height: '20px' }}
          />
        )}
      </div>

      {/* Show participant name with owner icon */}
      <SidebarParticipantName>
        {ownerSessionId && participantId === ownerSessionId && (
          <span style={{ marginRight: '4px', opacity: 0.95 }} title="Room owner">👑</span>
        )}
        {participantName}
        {isHandRaised && ' ✋'}
      </SidebarParticipantName>

      {/* Show screen share overlay for any participant who is sharing */}
      {isScreenSharing && (
        <ScreenShareOverlay
          className="screen-share-overlay"
          onClick={() => {
            // Find the screen share track for this participant
            const screenTrack = screenShareTracks.find(
              (t) => t.participant.identity === participantId
            );
            if (screenTrack) {
              onScreenShareClick(participantId, screenTrack.trackSid);
            }
          }}
        >
          {(() => {
            const track = screenShareTracks.find(
              (t) => t.participant.identity === participantId
            );
            return track ? (
              <div style={{ width: '100%', height: '100%' }}>
                {/* @ts-ignore - VideoTrack returns ReactNode but we need JSX.Element */}
                <VideoTrack trackRef={track} />
              </div>
            ) : (
              <div
                style={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '8px',
                }}
              >
                📺
              </div>
            );
          })()}
          <ScreenShareLabel className="screen-share-label">
            {isLocal ? 'YOUR' : 'SHARED'}
          </ScreenShareLabel>
        </ScreenShareOverlay>
      )}

      {/* Show whiteboard overlay for any participant using a whiteboard (same pattern as screenshare) */}
      {isUsingWhiteboard && !isScreenSharing && (
        <ScreenShareOverlay
          className="screen-share-overlay"
          onClick={() => onWhiteboardClick?.(participantId)}
          style={{
            border: '2px solid rgba(99, 102, 241, 0.8)',
          }}
        >
          <div
            style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontSize: '12px',
            }}
          >
            📝
          </div>
          <ScreenShareLabel className="screen-share-label" style={{ background: 'rgba(99, 102, 241, 0.9)', fontSize: '6px' }}>
            WHITEBOARD
          </ScreenShareLabel>
        </ScreenShareOverlay>
      )}
    </VideoWrapper>
  );
});

/** SC-375: a gallery grid tile — the sidebar tile's content in a frame that fills its cell. */
export const ParticipantVideoGallery = React.forwardRef<HTMLDivElement, ParticipantVideoOverlayProps>(
  (props, ref) => <ParticipantVideoSidebar {...props} ref={ref} variant="gallery" />
);
